import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchEvent, generatePaymentLink } from "../utils/firebase";
import { Timestamp } from "firebase/firestore";
import {
    formatCurrency,
    formatFirestoreTimestamp,
    formatFirestoreTimestampToTime,
    toTimestamp,
} from "../utils/common";
import {
    Banknote,
    Calendar,
    CreditCard,
    DollarSign,
    Mail,
    Phone,
    Ticket,
    User,
} from "lucide-react";
import { twMerge } from "tailwind-merge";
import { RETURN_AND_REFUND_POLICY_PATH } from "../App";
import toast from "react-hot-toast";
import SelectInput from "../components/select-input";
import { countryDialCodes } from "../constants";

const PurchaseTicket = () => {
    const paymentMethods = [
        {
            id: "tng",
            name: "Touch 'n Go eWallet",
            logos: ["/assets/logo/logo-tngd.png"],
        },
        {
            id: "masterCard",
            name: "Debit/Credit Card",
            logos: [
                "/assets/logo/logo-mastercard.png",
                "/assets/logo/logo-visa.png",
            ],
        },
        {
            id: "fpx",
            name: "FPX Online Banking",
            logos: ["/assets/logo/logo-fpx.png"],
        },
    ];
    const [event, setEvent] = useState(null);
    const { t } = useTranslation();
    const { id } = useParams();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [selectedDialCode, setSelectedDialCode] = useState("+60");
    const [
        isReadAndUnderstoodInstructions,
        setIsReadAndUnderstoodInstructions,
    ] = useState(false);
    const [
        isReadAndAgreeWithReturnAndRefundPolicy,
        setIsReadAndAgreeWithReturnAndRefundPolicy,
    ] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Live stream ticket sales are closed once the event has ended or the
    // purchase deadline has passed. The Firestore Timestamp may arrive as
    // `.seconds` (events list) or `._seconds` (direct fetch), so handle both.
    const purchaseDeadlineSeconds =
        event?.liveStreamTicketPurchaseDeadline?.seconds ??
        event?.liveStreamTicketPurchaseDeadline?._seconds ??
        null;
    const isSaleClosed =
        !!event &&
        (event.isEnded === true ||
            event.isTest === true ||
            !purchaseDeadlineSeconds ||
            purchaseDeadlineSeconds * 1000 < Date.now());

    const handleSubmit = async () => {
        if (isSaleClosed) {
            return toast.error(t("purchaseTicket.errors.salesEnded"));
        }
        if (!name) {
            return toast.error(t("purchaseTicket.errors.nameRequired"));
        }
        if (!phoneNumber) {
            return toast.error(t("purchaseTicket.errors.phoneRequired"));
        }
        if (!email) {
            return toast.error(t("purchaseTicket.errors.emailRequired"));
        }
        if (!selectedPaymentMethod) {
            return toast.error(
                t("purchaseTicket.errors.paymentMethodRequired")
            );
        }
        if (
            !isReadAndUnderstoodInstructions ||
            !isReadAndAgreeWithReturnAndRefundPolicy
        ) {
            return toast.error(
                t("purchaseTicket.errors.acknowledgementRequired")
            );
        }

        setIsSubmitting(true);
        try {
            const lang = localStorage.getItem("i18nextLng") || "en";
            let formattedPhoneNumber = phoneNumber;
            // For Malaysia (+60), remove leading 0 if present for user-friendly experience
            if (selectedDialCode === "+60" && phoneNumber.startsWith("0")) {
                formattedPhoneNumber = phoneNumber.substring(1);
            }
            const fullPhoneNumber = `${selectedDialCode}${formattedPhoneNumber}`;

            const paymentLinkResponse = await generatePaymentLink({
                eventId: id,
                paymentMethod: selectedPaymentMethod,
                email: email,
                name: name,
                phoneNumber: fullPhoneNumber,
                lang: lang ?? "en",
            });
            if (!paymentLinkResponse.data || !!paymentLinkResponse.data.error) {
                toast.error(paymentLinkResponse.data.error);
                return;
            }
            const paymentUrl = paymentLinkResponse.data.paymentUrl;

            window.location.href = paymentUrl;
        } catch (error) {
            console.error("Payment link generation error:", error);
            toast.error(t("purchaseTicket.errors.paymentFailed"));
        } finally {
            setIsSubmitting(false);
        }
    };
    useEffect(() => {
        (async () => {
            setIsLoading(true);
            if (location?.state?.id === id) {
                setEvent({
                    ...location?.state,
                    dateTime: Timestamp.fromMillis(
                        location?.state?.dateTime?.seconds * 1000
                    ),
                    registerExpiredDateTime: Timestamp.fromMillis(
                        location?.state?.registerExpiredDateTime?.seconds * 1000
                    ),
                    liveStreamTicketPurchaseDeadline: toTimestamp(
                        location?.state?.liveStreamTicketPurchaseDeadline
                    ),
                });
                setIsLoading(false);
            } else {
                try {
                    const fetchedEvent = await fetchEvent({
                        id: id,
                    });

                    if (!!fetchedEvent.data) {
                        setEvent({
                            ...fetchedEvent.data,
                            dateTime: !fetchedEvent.data.dateTime?._seconds
                                ? null
                                : Timestamp.fromMillis(
                                      fetchedEvent.data.dateTime?._seconds *
                                          1000
                                  ),
                            registerExpiredDateTime: !fetchedEvent.data
                                .registerExpiredDateTime?._seconds
                                ? null
                                : Timestamp.fromMillis(
                                      fetchedEvent.data.registerExpiredDateTime
                                          ?._seconds * 1000
                                  ),
                            createdAt: !fetchedEvent.data.createdAt?._seconds
                                ? null
                                : Timestamp.fromMillis(
                                      fetchedEvent.data.createdAt?._seconds *
                                          1000
                                  ),
                            updateAt: !fetchedEvent.data.updateAt?._seconds
                                ? null
                                : Timestamp.fromMillis(
                                      fetchedEvent.data.updateAt?._seconds *
                                          1000
                                  ),
                            liveStreamTicketPurchaseDeadline: toTimestamp(
                                fetchedEvent.data
                                    .liveStreamTicketPurchaseDeadline
                            ),
                        });
                        setIsLoading(false);
                    } else {
                        setIsLoading(false);
                        navigate("/404");
                    }
                } catch (err) {
                    setIsLoading(false);
                    navigate("/404");
                }
            }
        })();
    }, [id, location?.state, navigate]);

    return (
        <>
            <div className="page-size flex-center flex-col gap-4 justify-start p-2 md:p-4 h-full">
                {!isLoading && isSaleClosed && (
                    <div className="alert alert-error max-w-[1000px] w-full">
                        <span>{t("purchaseTicket.salesEndedNotice")}</span>
                    </div>
                )}
                <div className="flex flex-col-reverse md:flex-row max-w-[1000px] w-full p-0 md:p-4 gap-4 border justify-between">
                    <div className="flex flex-col gap-4 p-4 w-full">
                        <p className="text-2xl font-bold inline-flex items-center gap-2">
                            <Ticket size={24} />
                            <span>{t("purchaseTicket.productDetails")}</span>
                        </p>
                        <div className="flex-center items-start text-lg gap-2 w-full">
                            {isLoading ? (
                                <div className="w-full">
                                    <div className="flex flex-col gap-2">
                                        <div className="skeleton h-5 w-40" />
                                        <div className="skeleton h-6 w-72" />
                                    </div>
                                    <div className="mt-4 flex flex-col gap-2">
                                        <div className="skeleton h-5 w-40" />
                                        <div className="skeleton h-6 w-32" />
                                    </div>
                                    <div className="mt-4">
                                        <div className="skeleton h-5 w-44 mb-2" />
                                        <div className="bg-white text-black p-4 rounded-md w-full">
                                            <div className="skeleton h-4 w-3/4 mb-2" />
                                            <div className="skeleton h-4 w-2/3 mb-2" />
                                            <div className="skeleton h-4 w-4/5 mb-2" />
                                            <div className="skeleton h-4 w-1/2 mb-2" />
                                        </div>
                                    </div>
                                    <div className="divider" />
                                    <div className="flex flex-row w-full justify-between items-center text-xl">
                                        <div className="skeleton h-6 w-28" />
                                        <div className="skeleton h-6 w-20" />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <p>
                                        <span>
                                            {t("purchaseTicket.productName")}{" "}
                                        </span>
                                        <span className="font-bold">
                                            {t(
                                                "purchaseTicket.liveStreamTicketFor"
                                            )}{" "}
                                            {event?.name}
                                        </span>
                                    </p>
                                    <p>
                                        <span>
                                            {t("purchaseTicket.productPrice")}{" "}
                                        </span>
                                        <span className="font-bold">
                                            {formatCurrency(
                                                event?.liveStreamTicketPrice ??
                                                    0
                                            )}
                                        </span>
                                    </p>
                                    <p>{t("purchaseTicket.instructions")} </p>
                                    <div className="bg-white text-black p-4 rounded-md w-full ">
                                        <p>
                                            {t("purchaseTicket.instruction1")}
                                        </p>
                                        <p>
                                            {t("purchaseTicket.instruction2")}
                                        </p>
                                        <p>
                                            {t("purchaseTicket.instruction3")}
                                        </p>
                                        <p>
                                            {t("purchaseTicket.instruction4")}
                                        </p>
                                        <ul className="list-disc list-inside pl-4">
                                            <li>
                                                {t(
                                                    "purchaseTicket.instruction4a"
                                                )}
                                            </li>
                                            <li>
                                                {t(
                                                    "purchaseTicket.instruction4b"
                                                )}
                                            </li>
                                            <li>
                                                {t(
                                                    "purchaseTicket.instruction4c"
                                                )}
                                            </li>
                                            <li>
                                                {t(
                                                    "purchaseTicket.instruction4d"
                                                )}
                                            </li>
                                        </ul>
                                        <p>
                                            <b>
                                                {t(
                                                    "purchaseTicket.instruction5"
                                                )}
                                            </b>
                                        </p>
                                    </div>
                                    <div className="divider" />
                                    <div className="flex flex-row w-full justify-between items-center text-xl">
                                        <p>{t("purchaseTicket.totalPrice")} </p>
                                        <p className="font-bold">
                                            {formatCurrency(
                                                event?.liveStreamTicketPrice ??
                                                    0
                                            )}
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="divider m-0 md:hidden" />
                    <div className="flex flex-col items-start gap-4 p-4 min-w-[300px]">
                        <p className="text-2xl font-bold">
                            {t("purchaseTicket.event")}
                        </p>
                        {isLoading ? (
                            <>
                                <div className="skeleton w-full max-w-sm h-64" />
                                <div className="skeleton h-6 w-48" />
                                <div className="flex items-center gap-2 w-full max-w-sm">
                                    <div className="skeleton h-5 w-5 rounded-full" />
                                    <div className="skeleton h-5 w-40" />
                                </div>
                            </>
                        ) : (
                            <>
                                {!!event?.coverImage && (
                                    <img
                                        src={event.coverImage}
                                        alt={event?.name}
                                        className="w-full object-contain object-center max-w-sm"
                                    />
                                )}
                                <p className="text-2xl font-bold">
                                    {event?.name}
                                </p>
                                <p className="text-lg inline-flex items-center gap-2">
                                    <Calendar />
                                    <span>
                                        {formatFirestoreTimestamp(
                                            event?.dateTime
                                        )}
                                        ,{" "}
                                        {formatFirestoreTimestampToTime(
                                            event?.dateTime
                                        )}
                                    </span>
                                </p>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex flex-col gap-4 border p-4 w-full max-w-[1000px]">
                    <p className="text-2xl font-bold inline-flex items-center gap-2">
                        <DollarSign size={24} />
                        <span>{t("purchaseTicket.checkout")}</span>
                    </p>
                    <p className="text-lg">{t("purchaseTicket.enterName")}</p>
                    <label
                        htmlFor="name"
                        className="w-full input input-bordered flex flex-row items-center gap-4"
                    >
                        <User />
                        <input
                            type="text"
                            id="name"
                            className="w-full"
                            placeholder={t("purchaseTicket.placeholders.name")}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={isLoading}
                        />
                    </label>
                    <p className="text-lg">{t("purchaseTicket.enterPhone")}</p>
                    <label
                        className="flex gap-0 w-full input input-bordered pl-2"
                        htmlFor="phoneNumber"
                    >
                        <div className="h-full aspect-square flex-center">
                            <Phone />
                        </div>
                        <div className="min-w-16 md:min-w-20">
                            <SelectInput
                                options={countryDialCodes.map(
                                    (item) => `${item.code} (${item.country})`
                                )}
                                defaultValue={selectedDialCode}
                                value={selectedDialCode}
                                onSelect={(value) => {
                                    setSelectedDialCode(value.split(" ")[0]);
                                }}
                                inputClassName={"!border-none"}
                                placeholder="+60"
                            />
                        </div>
                        <div className="divider divider-horizontal m-0" />
                        <input
                            type="tel"
                            id="phoneNumber"
                            className="input grow"
                            placeholder={t("purchaseTicket.placeholders.phone")}
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            disabled={isLoading}
                        />
                    </label>
                    <p className="text-lg">{t("purchaseTicket.enterEmail")}</p>
                    <label
                        htmlFor="email"
                        className="w-full input input-bordered flex flex-row items-center gap-4"
                    >
                        <Mail />
                        <input
                            type="email"
                            id="email"
                            className="w-full"
                            placeholder={t("purchaseTicket.placeholders.email")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isLoading}
                        />
                    </label>
                    <p className="text-lg">
                        {t("purchaseTicket.selectPayment")}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {paymentMethods.map((paymentMethod) => (
                            <button
                                key={paymentMethod.id}
                                className={twMerge(
                                    "btn py-3 px-4 btn-primary",
                                    selectedPaymentMethod === paymentMethod.id
                                        ? "opacity-100"
                                        : "opacity-50"
                                )}
                                onClick={() =>
                                    setSelectedPaymentMethod(paymentMethod.id)
                                }
                                disabled={isLoading}
                            >
                                {paymentMethod.logos.map((logo) => (
                                    <img
                                        src={logo}
                                        alt={paymentMethod.name}
                                        className="h-full object-contain object-center rounded-sm"
                                    />
                                ))}
                                <span>
                                    {t(
                                        `purchaseTicket.paymentMethods.${paymentMethod.id}`
                                    )}
                                </span>
                            </button>
                        ))}
                    </div>
                    <p>{t("purchaseTicket.acknowledgement")}</p>
                    <label className="cursor-pointer inline-flex items-center gap-2">
                        <input
                            type="checkbox"
                            value={isReadAndUnderstoodInstructions}
                            onChange={() => {
                                setIsReadAndUnderstoodInstructions(
                                    !isReadAndUnderstoodInstructions
                                );
                            }}
                            className="checkbox"
                        />
                        <span className="w-full ">
                            {t("purchaseTicket.readInstructions")}
                        </span>
                    </label>
                    <label className="cursor-pointer inline-flex items-center gap-2">
                        <input
                            type="checkbox"
                            value={isReadAndAgreeWithReturnAndRefundPolicy}
                            onChange={() => {
                                setIsReadAndAgreeWithReturnAndRefundPolicy(
                                    !isReadAndAgreeWithReturnAndRefundPolicy
                                );
                            }}
                            className="checkbox"
                        />
                        <span className="">
                            {t("purchaseTicket.agreePolicy")}{" "}
                            <Link
                                target="_self"
                                className="underline bold text-blue-500"
                                to={RETURN_AND_REFUND_POLICY_PATH}
                            >
                                {t("purchaseTicket.returnAndRefundPolicy")}
                            </Link>
                        </span>
                    </label>
                    <button
                        className="btn btn-primary text-bold"
                        onClick={handleSubmit}
                        disabled={isSubmitting || isLoading || isSaleClosed}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="loading loading-bars"></span>
                            </>
                        ) : isSaleClosed ? (
                            t("purchaseTicket.salesEnded")
                        ) : (
                            t("purchaseTicket.proceedToPayment")
                        )}
                    </button>
                </div>
            </div>
        </>
    );
};

export default PurchaseTicket;
